/**
 * @fileoverview A aba Fontes do agente: o site de onde veio a base, os arquivos
 * (lidos e enviados ao cliente), as respostas prontas ("quando perguntarem
 * isto, responda aquilo"), a pesquisa na internet só nos sites listados e o
 * catálogo das lojas do WooCommerce ativas. Os arquivos sobem para a pasta dos
 * fluxos (o servidor só aceita arquivos de lá).
 */

import React, { useRef, useState } from "react";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@material-ui/core/Checkbox";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";
import { FileText, Plus, Trash2, Upload } from "lucide-react";

import api from "../../../../services/api";
import toastError from "../../../../errors/toastError";
import { i18n } from "../../../../translate/i18n";
import useStyles from "../TriageManager/styles";
import SectionTitle from "../TriageManager/InfoTip";
import useAgentStyles from "./styles";

const t = (key) => i18n.t(`agentManager.editor.sources.${key}`);

const sizeOf = (bytes) => {
  if (!bytes) return "";
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const SourcesTab = ({ form, onChange, readOnly, stores }) => {
  const classes = useStyles();
  const agentClasses = useAgentStyles();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const field = (props) => (
    <TextField className={classes.field} variant="outlined" size="small" fullWidth InputLabelProps={{ shrink: true }} disabled={readOnly} {...props} />
  );

  const upload = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/ai-agents/knowledge-files", body, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({
        knowledgeFiles: [
          ...form.knowledgeFiles,
          { id: data.id, name: data.name, originalName: data.name, url: data.url, path: data.path, size: data.size, type: file.type || null },
        ],
      });
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  const setCase = (index, changes) => onChange({ cases: form.cases.map((item, i) => (i === index ? { ...item, ...changes } : item)) });

  const toggleStore = (id) =>
    onChange({
      wooIntegrationIds: form.wooIntegrationIds.includes(id) ? form.wooIntegrationIds.filter((value) => value !== id) : [...form.wooIntegrationIds, id],
    });

  return (
    <div className={agentClasses.columns}>
      <div className={agentClasses.column}>
        <div className={agentClasses.section}>
          <SectionTitle title={t("siteTitle")} info={t("siteInfo")} />
          {field({ placeholder: t("sitePlaceholder"), value: form.sourceUrl, onChange: (event) => onChange({ sourceUrl: event.target.value }), inputProps: { maxLength: 500 } })}
        </div>

        <div className={agentClasses.section}>
          <SectionTitle title={t("filesTitle")} info={t("filesInfo")} />
          {form.knowledgeFiles.length === 0 && <div className={agentClasses.muted}>{t("filesEmpty")}</div>}
          <div className={agentClasses.list}>
            {form.knowledgeFiles.map((file) => (
              <div key={file.id} className={agentClasses.fileRow}>
                <FileText />
                <span className={agentClasses.fileName}>{file.name || file.originalName}</span>
                <span className={agentClasses.fileMeta}>{sizeOf(file.size)}</span>
                {!readOnly && (
                  <button
                    type="button"
                    className={classes.iconBtn}
                    onClick={() => onChange({ knowledgeFiles: form.knowledgeFiles.filter((item) => item.id !== file.id) })}
                    aria-label={t("remove")}
                  >
                    <Trash2 />
                  </button>
                )}
              </div>
            ))}
          </div>
          {!readOnly && (
            <div>
              <input ref={fileInputRef} type="file" hidden onChange={upload} />
              <button type="button" className={classes.btnSecondary} onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={uploading}>
                {uploading ? <CircularProgress size={14} /> : <Upload />}
                {uploading ? t("uploading") : t("filesAdd")}
              </button>
            </div>
          )}
        </div>

        <div className={agentClasses.section}>
          <SectionTitle title={t("webTitle")} info={t("webInfo")} />
          <label className={agentClasses.switchRow}>
            {t("webToggle")}
            <Switch
              size="small"
              color="primary"
              checked={form.webSearchEnabled}
              onChange={(event) => onChange({ webSearchEnabled: event.target.checked })}
              disabled={readOnly}
            />
          </label>
          {form.webSearchEnabled &&
            field({
              label: t("webDomains"),
              placeholder: t("webDomainsPlaceholder"),
              value: form.webSearchDomains,
              onChange: (event) => onChange({ webSearchDomains: event.target.value }),
              inputProps: { maxLength: 1000 },
            })}
        </div>

        <div className={agentClasses.section}>
          <SectionTitle title={t("wooTitle")} info={t("wooInfo")} />
          {stores.length === 0 ? (
            <div className={agentClasses.muted}>{t("wooEmpty")}</div>
          ) : (
            stores.map((store) => (
              <label key={store.id} className={agentClasses.storeRow}>
                <Checkbox
                  size="small"
                  color="primary"
                  checked={form.wooIntegrationIds.includes(store.id)}
                  onChange={() => toggleStore(store.id)}
                  disabled={readOnly}
                  style={{ padding: 4 }}
                />
                {store.name}
              </label>
            ))
          )}
        </div>
      </div>
      <div className={agentClasses.column}>
        <div className={agentClasses.section}>
          <SectionTitle title={t("casesTitle")} info={t("casesInfo")} />
          {form.cases.length === 0 && <div className={agentClasses.muted}>{t("casesEmpty")}</div>}
          <div className={agentClasses.list}>
            {form.cases.map((item, index) => (
              <div key={item.id} className={agentClasses.caseCard}>
                <div className={agentClasses.caseFields}>
                  {field({
                    label: t("caseWhen"),
                    placeholder: t("caseWhenPlaceholder"),
                    value: item.name,
                    onChange: (event) => setCase(index, { name: event.target.value }),
                    inputProps: { maxLength: 200 },
                  })}
                  {field({
                    label: t("caseAnswer"),
                    placeholder: t("caseAnswerPlaceholder"),
                    value: item.content,
                    onChange: (event) => setCase(index, { content: event.target.value }),
                    multiline: true,
                    minRows: 2,
                    inputProps: { maxLength: 20000 },
                  })}
                </div>
                {!readOnly && (
                  <button type="button" className={classes.iconBtn} onClick={() => onChange({ cases: form.cases.filter((_, i) => i !== index) })} aria-label={t("remove")}>
                    <Trash2 />
                  </button>
                )}
              </div>
            ))}
          </div>
          {!readOnly && (
            <div>
              <button
                type="button"
                className={classes.btnSecondary}
                onClick={() => onChange({ cases: [...form.cases, { id: `new-${Date.now()}`, name: "", content: "", summary: "" }] })}
              >
                <Plus /> {t("casesAdd")}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SourcesTab;
